import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import Reveal from "./Reveal";
import { MEMBERSHIPS } from "@/data/plans";

export default function PricingPlans() {
  const { section, plans } = MEMBERSHIPS;

  return (
    <section className="py-20 bg-white border-t border-ink/10" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center mb-14">
          <h2 className="font-body font-extrabold text-2xl sm:text-3xl lg:text-4xl text-ink tracking-tight" data-testid="pricing-title">
            {section.title}
          </h2>
          <p className="mt-2 text-brand font-semibold text-base sm:text-lg" data-testid="pricing-subtitle">
            {section.subtitle}
          </p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {plans.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.08} className="h-full">
              <div
                className={`relative h-full flex flex-col rounded-2xl p-6 pt-9 border ${p.tint} ${
                  p.highlighted
                    ? "shadow-[0_18px_50px_-24px_rgba(178,132,0,0.45)]"
                    : "shadow-[0_14px_40px_-24px_rgba(27,42,74,0.3)]"
                }`}
                data-testid={`plan-card-${i}`}
              >
                {p.tag && (
                  <span className={`absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-extrabold tracking-[0.15em] px-3.5 py-1 rounded-full whitespace-nowrap ${p.badgeClass || "bg-brand text-white"}`} data-testid={`plan-tag-${i}`}>
                    {p.tag}
                  </span>
                )}
                {p.savingsBadge && (
                  <span className="absolute top-3 right-3 bg-brand text-white text-[10px] font-extrabold px-2.5 py-1 rounded-full" data-testid={`plan-badge-${i}`}>
                    {p.savingsBadge}
                  </span>
                )}
                <h3 className="font-body font-bold text-lg text-ink" data-testid={`plan-name-${i}`}>{p.name}</h3>
                <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                  <span className="font-body font-extrabold text-3xl text-ink" data-testid={`plan-price-${i}`}>{p.price}</span>
                  {p.priceSuffix && <span className="text-sm text-muted">{p.priceSuffix}</span>}
                  {p.valuePrice && <span className="text-sm text-ink/60 line-through font-medium" data-testid={`plan-value-${i}`}>{p.valuePrice}</span>}
                </div>
                <ul className="mt-4 space-y-2" data-testid={`plan-includes-${i}`}>
                  {p.perks.map((perk, pi) => {
                    const isFree = /free/i.test(perk);
                    return (
                      <li key={perk} className="flex items-start gap-2 text-[13px] text-ink/80 leading-snug">
                        <Check size={13} className={`mt-0.5 shrink-0 ${isFree && p.highlighted ? "text-gold" : "text-brand"}`} />
                        {isFree && p.highlighted ? (
                          <span className="inline-flex rounded-full bg-gold/20 border border-gold/60 text-[#8a6d00] text-[11px] font-bold px-2 py-0.5">{perk}</span>
                        ) : (
                          perk
                        )}
                      </li>
                    );
                  })}
                </ul>
                <div className="mt-auto pt-6">
                  <Link
                    to={`/play-first-pay-later?membership=${encodeURIComponent(p.name)}`}
                    data-testid={`plan-cta-${i}`}
                    className="block text-center rounded-full bg-brand text-white font-semibold text-sm py-3 hover:bg-ink transition-colors duration-300"
                  >
                    {p.ctaLabel}
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="text-center mt-12">
          <p className="font-body font-bold text-ink" data-testid="pricing-footer-line">{section.footerLine}</p>
          <p className="text-sm text-muted mt-2" data-testid="pricing-footer-contact">
            <a href={`tel:+91${section.phone}`} className="text-brand font-semibold hover:underline" data-testid="pricing-footer-phone">Contact +91 {section.phone}</a> | Location: {section.location}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
